import React from "react";
import { RiGroupLine } from "@remixicon/react";
import { Typography, Card, TickIcon } from "../../atoms";
import StatusWithBar from "../../atoms/StatusWithBar";
import type { AudienceFitMetric } from "../../organisms/ContentQualityWidget/types";
import styles from "./index.style.module.css";

export interface AudienceFitCardProps {
  confidenceValue: number;
  status: "Healthy" | "Stable" | "Critical" | "Fair" | "Risk";
  metrics: AudienceFitMetric[];
  worksWell: string[];
  className?: string;
}

const getMetricStatus = (
  value: number,
): { text: string; color: string; bgColor: string } => {
  // Score thresholds: 8-10 (80-100%) = Healthy, 5-7 (50-79%) = Stable, <5 (<50%) = Critical
  if (value >= 80) {
    return {
      text: "Healthy",
      color: "var(--border-new-success, #008253)",
      bgColor: "var(--border-new-success, #008253)",
    };
  } else if (value >= 50) {
    return {
      text: "Stable",
      color: "var(--border-new-warning, #ff7900)",
      bgColor: "var(--border-new-warning, #ff7900)",
    };
  }
  return {
    text: "At Risk",
    color: "var(--border-new-critical, #a20f2c)",
    bgColor: "var(--border-new-critical, #a20f2c)",
  };
};

const AudienceFitCard: React.FC<AudienceFitCardProps> = ({
  confidenceValue,
  status,
  metrics,
  worksWell,
  className = "",
}) => {
  let filledSegments = 2;
  if (status === "Healthy") {
    filledSegments = 8;
  } else if (status === "Stable" || status === "Fair") {
    filledSegments = 5;
  }

  return (
    <Card variant="v2" classes={`${styles.audienceFitCard} ${className}`}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <div className={styles.titleRow}>
            <RiGroupLine size={16} color="#3F3F46" />
            <Typography
              variant="text-md"
              weight="bold"
              className={styles.title}
            >
              Audience Fit
            </Typography>
          </div>
          <div className={styles.confidenceText}>
            <span className={styles.confidenceLabel}>Confidence Level:</span>
            <span className={styles.confidenceValue}>{confidenceValue}%</span>
          </div>
        </div>
        <StatusWithBar status={status} filledSegments={filledSegments} />
      </div>

      <div className={styles.content}>
        <div className={styles.metricsSection}>
          {metrics.map((metric) => {
            const metricStatus = getMetricStatus(metric.value);
            return (
              <div key={metric.label} className={styles.metricCard}>
                <span className={styles.metricLabel}>{metric.label}</span>
                <div className={styles.metricScoreContainer}>
                  <span className={styles.metricStatusText}>
                    {metricStatus.text}
                  </span>
                  <div className={styles.metricProgressBar}>
                    <div
                      className={styles.metricProgressFill}
                      style={{
                        width: `${metric.value}%`,
                        backgroundColor: metricStatus.bgColor,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {worksWell.length > 0 && (
          <div className={styles.feedbackSection}>
            <Typography
              variant="text-s"
              weight="bold"
              className={styles.feedbackTitle}
            >
              What Works Well
            </Typography>
            <div className={styles.feedbackList}>
              {worksWell.map((item, index) => (
                <div key={`${item}-${index}`} className={styles.feedbackItem}>
                  <TickIcon className={styles.feedbackIcon} />
                  <span className={styles.feedbackText}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AudienceFitCard;
